import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { Image } from '@/atoms/Image'
import { AsLabel, LabelLG, ParagraphSM, TitleSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { options } from '@/services/ContentfulService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '../GuildSignupContext'

interface GuildTicketsCalloutSectionProps {
  isScrollingLayout?: boolean
  landing?: boolean
  isPremiumGuildUser?: boolean
}

export const GuildTicketsCalloutSection: React.FC<GuildTicketsCalloutSectionProps> = ({
  isScrollingLayout = false,
  landing = false,
  isPremiumGuildUser = false,
}) => {
  const { t } = useTranslate('common')
  const { guildTicketsSection } = useGuildSignupContext()
  const { title, description, ticketsImageUrl } = guildTicketsSection || {}

  return (
    <section
      className={classNames(
        'relative w-full pb-16 lg:pb-20',
        isScrollingLayout
          ? '-mt-[12vh] se:-mt-20 md:-mt-40 lg:-mt-38 z-50'
          : '-mt-[11vh] se:-mt-50 md:-mt-40 lg:-mt-36',
      )}
    >
      {/*Mobile*/}
      <div className="relative sm:hidden">
        <div
          className="mx-6 mb-1 max-w-[1283px] rounded-2xl bg-transparent p-[1px] 3xl:mx-auto"
          style={{
            backgroundImage:
              'linear-gradient(166.78deg, #EDD7C6 1.42%, #D6A177 14.6%, #FEDEC6 39.98%, #D48D59 60.48%, #A66141 85.7%, #612B12 94.6%)',
          }}
        >
          <div className="relative">
            <div className="flex rounded-2xl bg-core-gray-900 text-white">
              <div className="mx-8 -mt-16 mb-1 rounded-2xl p-[1px]">
                <div className="relative flex flex-col items-center justify-center">
                  {ticketsImageUrl && (
                    <Image
                      className="w-[160px]"
                      width={269}
                      height={325}
                      alt="Guild Tickets"
                      src={getCloudinaryImageUrl({ path: ticketsImageUrl })}
                    />
                  )}
                  <div className="flex py-2 text-center text-white">
                    <div className="w-full max-w-[662px] sm:w-3/5">
                      {title && (
                        <TitleSM
                          weight="bold"
                          className="md:photon-title-md lg:photon-heading-sm xl:photon-heading-md mb-3"
                        >
                          {title}
                        </TitleSM>
                      )}
                      {description?.json && (
                        <ParagraphSM
                          color="core-gray-400"
                          weight="medium"
                          className="lg:photon-paragraph-lg max-w-[662px]"
                        >
                          {documentToReactComponents(description.json, options)}
                        </ParagraphSM>
                      )}
                    </div>
                  </div>
                  {landing && !isPremiumGuildUser && (
                    <LinkButton
                      href={paths.guild.join}
                      variant={'core-gray-900'}
                      className={classNames(
                        'btn btn-standard mb-4 my-0 lg:w-2/5 rounded-lg py-3 min-h-[56px] !bg-[#FFA41C] !border-[#FFA41C]',
                      )}
                    >
                      <LabelLG
                        as={AsLabel}
                        color={'black'}
                        weight="semibold"
                        className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                      >
                        {t('joinForTickets', 'Join For Tickets')}
                      </LabelLG>
                    </LinkButton>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*Desktop*/}
      <div className="hidden bg-transparent sm:block">
        <div
          className={classNames('mx-12 mb-1 rounded-2xl bg-transparent p-[1px] 3xl:mx-auto 3xl:max-w-[2000px]')}
          style={{
            backgroundImage:
              'linear-gradient(166.78deg, #EDD7C6 1.42%, #D6A177 14.6%, #FEDEC6 39.98%, #D48D59 60.48%, #A66141 85.7%, #612B12 94.6%)',
          }}
        >
          <div className="relative">
            <div className="flex rounded-2xl bg-core-gray-900 p-8 text-white">
              <div className="w-full sm:w-3/5">
                {title && (
                  <TitleSM
                    weight="bold"
                    className="md:photon-title-md lg:photon-heading-sm xl:photon-heading-md mb-3 max-w-[662px]"
                  >
                    {title}
                  </TitleSM>
                )}
                {description?.json && (
                  <ParagraphSM color="core-gray-400" weight="medium" className="lg:photon-paragraph-lg max-w-[662px]">
                    {documentToReactComponents(description.json, options)}
                  </ParagraphSM>
                )}
                {landing && !isPremiumGuildUser && (
                  <LinkButton
                    href={paths.guild.join}
                    variant={'core-gray-900'}
                    aria-labelledby="join-for-tickets-mobile"
                    className={classNames(
                      'btn btn-standard my-0 w-fit py-3 rounded-lg !bg-[#FFA41C] !border-[#FFA41C] 2xl:hidden',
                    )}
                  >
                    <LabelLG
                      as={AsLabel}
                      color={'black'}
                      weight="semibold"
                      id="join-for-tickets-mobile"
                      className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                    >
                      {t('joinForTickets', 'Join For Tickets')}
                    </LabelLG>
                  </LinkButton>
                )}
              </div>
              {landing && !isPremiumGuildUser && (
                <LinkButton
                  href={paths.guild.join}
                  variant={'core-gray-900'}
                  aria-labelledby="join-for-tickets-desktop"
                  className={classNames(
                    'btn btn-standard my-0 w-fit py-3 h-10 rounded-lg min-w-[250px] !bg-[#FFA41C] !border-[#FFA41C] hidden 2xl:flex 2xl:my-auto',
                  )}
                >
                  <LabelLG
                    as={AsLabel}
                    color={'black'}
                    weight="semibold"
                    id="join-for-tickets-desktop"
                    className="se:photon-label-md flex h-full cursor-pointer flex-row items-center justify-center gap-1"
                  >
                    {t('joinForTickets', 'Join For Tickets')}
                  </LabelLG>
                </LinkButton>
              )}
              <div className="relative ml-auto hidden w-1/3 sm:block">
                {ticketsImageUrl && (
                  <Image
                    className="absolute right-0 max-w-[435px] sm:-top-1/4 sm:block md:-top-1/2 lg:-top-3/4 lg:right-10 xl:-top-full 2xl:right-16 3xl:right-20"
                    width={269}
                    height={325}
                    alt="Guild Tickets"
                    src={getCloudinaryImageUrl({ path: ticketsImageUrl })}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
